import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { CoursesSection } from '@/components/sections/CoursesSection';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import ServiceCalloutBanner from '@/components/shared/ServiceCalloutBanner';
import { Button } from '@/components/ui/button';
import {
  Briefcase,
  MonitorPlay,
  UserRound,
  ArrowRight,
  ArrowDown,
  GraduationCap,
  TrendingUp,
  Rocket,
} from 'lucide-react';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const FEATURES = [
  {
    icon: Briefcase,
    title: 'Working Industry Instructors',
    body: 'Your instructor is not a teacher. They are a practitioner who does this work every day. You learn what is actually used in the industry right now.',
  },
  {
    icon: MonitorPlay,
    title: 'Face to Face or Online — Your Choice',
    body: 'Sessions delivered at your preferred location or via video. You choose the format that works best for you — we adapt.',
  },
  {
    icon: UserRound,
    title: 'One to One Attention',
    body: 'No classrooms. No waiting for answers. Direct access to your instructor means you progress at the pace that is right for you.',
  },
];

const AUDIENCE = [
  {
    icon: Rocket,
    title: 'Career Changers',
    body: 'You are done with your current path and ready to build something better. We will give you the skills, the credentials, and the confidence to make the switch.',
  },
  {
    icon: TrendingUp,
    title: 'Upskilling Professionals',
    body: 'You are already in tech but the industry is moving fast. Stay ahead of the curve with training that reflects what employers actually need right now.',
  },
  {
    icon: GraduationCap,
    title: 'Recent Graduates',
    body: 'Your degree opened the door. Now you need the practical skills and the professional profile to walk through it. We will get you there.',
  },
];

function scrollToCourses(e) {
  e.preventDefault();
  const el = document.getElementById('courses');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const ACODIA_PROVIDER = {
  '@type': 'Organization',
  name: 'Acodia',
  url: 'https://acodia.co.uk',
  sameAs: 'https://acodia.co.uk',
};

const COURSE_CATALOG_SCHEMAS = [
  { name: 'Cybersecurity Fundamentals', description: 'Network security, threat analysis and incident response — the practical foundations you need to step into a cybersecurity role.' },
  { name: 'AI & Machine Learning', description: 'Python, TensorFlow, neural networks and data pipelines — hands-on ML taught by working instructors.' },
  { name: 'Data Analytics', description: 'SQL, Python, visualisation and statistical analysis for people moving into data-led roles.' },
  { name: 'Cloud Security Engineer', description: 'AWS and Azure security, IAM, Zero Trust, and container security — the advanced cloud security track.' },
  { name: 'Career Accelerator', description: 'CV engineering, interview preparation and networking — eight weeks to a meaningful salary boost.' },
  { name: 'Penetration Testing', description: 'Ethical hacking, web application testing and professional reporting — practitioner-level offensive security.' },
].map((c) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: c.name,
  description: c.description,
  provider: ACODIA_PROVIDER,
}));

export default function LearnPage() {
  useSEO({
    title: 'Learn | Acodia — Grow. Defend. Be Found.',
    description: 'Expert-led training in Cybersecurity, AI, Machine Learning, and Data Analytics. Face to face or online. One to one or small group. Built around your schedule.',
    path: '/learn',
    ogImage: `${SITE_URL}/images/og/og-learn.png`,
    jsonLd: COURSE_CATALOG_SCHEMAS,
  });

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }} data-testid="learn-page">
      <Navbar />

      {/* ── HERO — warmer violet radial mesh ── */}
      <section
        data-testid="learn-hero"
        className="relative pt-32 pb-20 sm:pt-40 sm:pb-28"
        style={{ background: 'var(--bg)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: [
              'radial-gradient(ellipse 70% 50% at 60% 30%, rgba(167,139,250,0.09) 0%, transparent 60%)',
              'radial-gradient(ellipse 50% 60% at 20% 70%, rgba(192,132,252,0.06) 0%, transparent 60%)',
            ].join(', '),
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--violet)' }}>
              Acodia Learn
            </p>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-5 max-w-[22ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: '#F0EDF8' }}
            >
              The Skills That Get You{' '}
              <span style={{ color: 'var(--violet)' }}>Hired</span> Are Learned Here.
            </h1>
            <p
              className="font-body font-light text-base sm:text-lg max-w-3xl mb-8"
              style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}
            >
              Expert-led training in Cybersecurity, AI, Machine Learning, and Data Analytics.
              Delivered face to face or online. One to one or small group. Built around your schedule and your goals — not ours.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="learn-hero-primary"
                onClick={scrollToCourses}
                className="btn-shimmer font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'var(--violet)', color: 'var(--white)' }}
              >
                Find Your Course <ArrowDown size={14} className="ml-2" />
              </Button>
              <a
                href="/pricing?service=learn"
                data-testid="learn-hero-secondary"
                className="inline-flex items-center font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'transparent', color: 'rgba(240,237,248,0.82)', border: '1px solid rgba(192,132,252,0.25)' }}
              >
                Get a Personalised Recommendation <ArrowRight size={14} className="ml-2" />
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── WHY EXPERT-LED SESSIONS — warm violet cards with hover accent bar ── */}
      <section data-testid="learn-why" className="py-14 sm:py-20" style={{ background: 'rgba(10,6,20,0.98)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <h2
                className="font-display font-extrabold tracking-[-0.03em] mb-4"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: '#F0EDF8' }}
              >
                This Is Not an Online Course. This Is{' '}
                <span style={{ color: 'var(--violet)' }}>Real</span> Training.
              </h2>
              <p className="font-body font-light text-base" style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}>
                No passive video libraries. No fixed-group-only delivery. No one-size-fits-all curriculum.
                Every session is live, every instructor is a working professional, and every programme is tailored
                to where you are and where you want to get to.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div
                  className="relative p-6 rounded-lg h-full transition-all group overflow-hidden"
                  style={{
                    background: 'rgba(20,10,35,0.9)',
                    border: '1px solid rgba(192,132,252,0.15)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(192,132,252,0.15)'; }}
                >
                  <div
                    className="flex items-center justify-center rounded mb-4 transition-colors"
                    style={{
                      width: 40,
                      height: 40,
                      background: 'rgba(192,132,252,0.1)',
                      border: '1px solid rgba(192,132,252,0.25)',
                    }}
                  >
                    <Icon size={18} className="transition-colors" style={{ color: 'var(--violet)' }} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: '#F0EDF8' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                  {/* hover accent bar */}
                  <span
                    aria-hidden
                    className="absolute bottom-0 left-0 right-0 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                    style={{ height: 2, background: 'linear-gradient(90deg, var(--violet), var(--a))' }}
                  />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── ALL SIX COURSES (reuses homepage CoursesSection → id="courses") ── */}
      <CoursesSection />

      <section className="py-8 sm:py-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCalloutBanner
            testId="learn-callout-banner"
            text="Not sure which course is right for you? Our team will give you a personalised recommendation in 15 minutes."
            ctaText="Get a free recommendation →"
            ctaUrl="/pricing?service=learn"
          />
        </div>
      </section>

      {/* ── WHO IT IS FOR — warm dark cards with left-border violet accent on hover ── */}
      <section data-testid="learn-audience" className="py-14 sm:py-20" style={{ background: 'rgba(8,5,15,0.98)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: '#F0EDF8' }}
            >
              Built for People Who Are Serious About Their Next Move.
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            {AUDIENCE.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div
                  data-testid={`learn-audience-${title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="p-6 rounded-lg h-full transition-all"
                  style={{
                    background: 'rgba(18,8,32,0.92)',
                    border: '1px solid rgba(192,132,252,0.12)',
                    borderLeft: '3px solid transparent',
                    backdropFilter: 'blur(20px)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(192,132,252,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(192,132,252,0.35)';
                    e.currentTarget.style.borderLeftColor = 'var(--violet)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(18,8,32,0.92)';
                    e.currentTarget.style.borderColor = 'rgba(192,132,252,0.12)';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded mb-4"
                    style={{
                      width: 40,
                      height: 40,
                      background: 'rgba(192,132,252,0.1)',
                      border: '1px solid rgba(192,132,252,0.25)',
                    }}
                  >
                    <Icon size={18} style={{ color: 'var(--violet)' }} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: '#F0EDF8' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA — warmer violet glow ── */}
      <section
        data-testid="learn-cta"
        className="py-16 sm:py-24 relative"
        style={{
          background: 'var(--bg)',
          boxShadow: 'inset 0 0 140px rgba(192,132,252,0.08)',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: '#F0EDF8' }}
            >
              Not Sure Which Course Is{' '}
              <span style={{ color: 'var(--violet)' }}>Right</span> for You?
            </h2>
            <p className="font-body font-light text-base mb-8" style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}>
              Answer five questions and we will tell you exactly where to start.
            </p>
            <a
              href="/pricing?service=learn"
              data-testid="learn-cta-button"
              className="btn-shimmer inline-flex items-center font-body text-sm h-12 px-7 rounded-[3px] transition-colors"
              style={{ background: 'var(--a)', color: 'var(--white)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--violet)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--a)'; }}
            >
              Get My Recommendation <ArrowRight size={14} className="ml-2" />
            </a>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
